/* eslint-disable max-len */
import { GAMES_LABELS_LIST } from "abstract/constants/game-ids"
import {
  canonicalGameId,
  catalogIdsAmongTimeBased,
  describeInvalidId,
  instanceIdsAmongTimeBased,
  isListedTimeBasedGame,
  timeBasedGameCaution,
  TIME_BASED_GAME_OPTIONS,
  invalidTimeBasedIds,
  syncedNotificationsPerGame,
} from "domains/specialEvents/timeBasedGames"
import {
  Button,
  Card,
  Collapse,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Select,
  Space,
  Switch,
  Tag,
  Tooltip,
  Typography,
} from "antd"
import { useEffect, useState } from "react"
import { NotificationsPerGame } from "domains/specialEvents/pages/main"
import { COUNTRY_CODES } from "utils/countryCodes"
import { US_REGION_CODES, getRegionNameByCode } from "utils/regionCodes"
// Direct path, not the "components" barrel: that barrel pulls in App.tsx and the api client,
// which makes this component unrenderable in a test.
import { UploadSoundComponent } from "components/upload/uploadSound"
import { HOTKEY_VALIDATION_MESSAGE, isValidHotkeyCombo } from "domains/specialEvents/hotkey"
import {
  convertDateRangeBetweenTimeZones,
  DEFAULT_SPECIAL_EVENT_TIME_ZONE,
  SPECIAL_EVENT_TIME_ZONE_OPTIONS,
} from "../../utils/timeZone"
import Styles from "./styles.module.css"

const { RangePicker } = DatePicker
const { Panel } = Collapse
const { Text } = Typography

type AdvancedField = "excludedIsps" | "restrictedToRegions" | "weight" | "defaultSoundFile" | "abSplit" | "notificationHotkey"

const ADVANCED_FIELD_LABELS: Record<AdvancedField, string> = {
  excludedIsps: "Excluded ISPs",
  restrictedToRegions: "Restricted regions",
  weight: "Weight",
  defaultSoundFile: "Default sound",
  abSplit: "A/B split",
  notificationHotkey: "Notification hotkey",
}

const hasArrayValue = (value?: unknown[]) => Boolean(value?.length)

export const MainInfoEditor = () => {
  const form = Form.useFormInstance()
  const defaultSoundFile = Form.useWatch("defaultSoundFile", form)
  const abSplit = Form.useWatch("abSplit", form)
  const excludedIsps = Form.useWatch("excludedIsps", form)
  const selectedTimeBasedGames: string[] = Form.useWatch("timeBasedGames", form) || []

  /*
   * Rendered from the value rather than raised as a form rule: a warningOnly rule never reached
   * the DOM, because every sync writes notificationsPerGame with setFieldsValue and rc-field-form
   * treats that external update as a reset of the field's in-flight validation. Shared-count notes
   * are deliberately left to the chip label — they are not a delivery risk, and warning on 36 rows
   * would train ops past the three that matter.
   */
  const timeBasedGameWarnings = selectedTimeBasedGames.flatMap(gameId => {
    const caution = timeBasedGameCaution(gameId)
    if (caution && !caution.startsWith("install count shared")) return [`${gameId}: ${caution}`]
    if (!isListedTimeBasedGame(gameId)) return [`${gameId} is not in the shipped game list — check the ID before selling it`]

    return []
  })
  const restrictedToRegions = Form.useWatch("restrictedToRegions", form)
  const weight = Form.useWatch("weight", form)
  const notificationHotkey = Form.useWatch("notificationHotkey", form)
  const selectedTimeZone = Form.useWatch("dateTimeZone", form) || DEFAULT_SPECIAL_EVENT_TIME_ZONE
  const [unlistedGameId, setUnlistedGameId] = useState("")
  const [unlistedError, setUnlistedError] = useState<string>()
  const [advancedFields, setAdvancedFields] = useState<Record<AdvancedField, boolean>>({
    excludedIsps: false,
    restrictedToRegions: false,
    weight: false,
    defaultSoundFile: false,
    abSplit: false,
    notificationHotkey: false,
  })

  useEffect(() => {
    setAdvancedFields(prev => ({
      excludedIsps: prev.excludedIsps || hasArrayValue(excludedIsps),
      restrictedToRegions: prev.restrictedToRegions || hasArrayValue(restrictedToRegions),
      weight: prev.weight || (typeof weight === "number" && weight !== 1),
      defaultSoundFile: prev.defaultSoundFile || Boolean(defaultSoundFile),
      abSplit: prev.abSplit || abSplit !== undefined,
      notificationHotkey: prev.notificationHotkey || Boolean(notificationHotkey),
    }))
  }, [abSplit, defaultSoundFile, excludedIsps, restrictedToRegions, weight, notificationHotkey])

  const syncNotificationsPerGame = (catalogIds: (string | number)[], timeBasedIds: (string | number)[]) => {
    const current: NotificationsPerGame[] = form.getFieldValue("notificationsPerGame") || []
    form.setFieldsValue({ notificationsPerGame: syncedNotificationsPerGame(current, catalogIds, timeBasedIds) })
  }

  const handleGameChange = (selectedGameIds: (string | number)[]) => {
    syncNotificationsPerGame(selectedGameIds, form.getFieldValue("timeBasedGames") || [])
  }

  /**
   * Adding an unlisted id is a separate, deliberate action. Folding it into the picker meant Enter
   * committed whatever had been typed so far, so searching for 21302 and pressing Enter at "2130"
   * silently created a campaign on a game that does not exist.
   */
  const addUnlistedGameId = () => {
    // A pasted list is the likely shape when someone copies ids out of a ticket.
    const typed = unlistedGameId.split(",").map(id => id.trim()).filter(Boolean)
    if (typed.length > 1) return typed.forEach(addOneUnlistedGameId)

    addOneUnlistedGameId(unlistedGameId)
  }

  const addOneUnlistedGameId = (raw: string) => {
    const canonical = canonicalGameId(raw)
    if (canonical === undefined) return setUnlistedError(describeInvalidId(raw))

    const [catalogId] = catalogIdsAmongTimeBased([canonical])
    if (catalogId) return setUnlistedError(`${catalogId} has game events — add it under Games instead`)

    const [instanceId] = instanceIdsAmongTimeBased([canonical])
    if (instanceId) return setUnlistedError(`${instanceId} looks like an instance ID — drop the last digit`)

    const current: string[] = form.getFieldValue("timeBasedGames") || []
    if (current.some(gameId => canonicalGameId(gameId) === canonical)) {
      return setUnlistedError(`${canonical} is already on this campaign`)
    }

    const next = [...current, canonical]
    form.setFieldsValue({ timeBasedGames: next })
    handleTimeBasedGameChange(next)
    setUnlistedGameId("")
    setUnlistedError(undefined)
  }

  const handleTimeBasedGameChange = (selectedGameIds: string[]) => {
    syncNotificationsPerGame(form.getFieldValue("games") || [], selectedGameIds)
  }

  // when country is changed, we also need to update the source images for each notification
  const handleCountryChange = (selectedCountryCodes: string[]) => {
    const selectedCountryCodesWithGlobal = ["global", ...selectedCountryCodes]
    const notificationsPerGame: NotificationsPerGame[] = form.getFieldValue(["notificationsPerGame"]) || []
    const updatedNotificationsPerGame = notificationsPerGame.map(notificationPerGame => {
      const updatedNotifications = notificationPerGame.notifications?.map(notification => {
        const updatedImagesByGeo = selectedCountryCodesWithGlobal.map(countryCode => {
          const existingImageByGeo = notification.sourceImagesByGeo?.find(imageByGeo => imageByGeo?.country === countryCode)

          return existingImageByGeo || { country: countryCode, imageSources: [] }
        })

        return { ...notification, sourceImagesByGeo: updatedImagesByGeo }
      })

      return { ...notificationPerGame, notifications: updatedNotifications }
    })

    form.setFieldsValue({ notificationsPerGame: updatedNotificationsPerGame })
  }

  const handleTimeZoneChange = (nextTimeZone: string) => {
    const dates = form.getFieldValue("dates")
    form.setFieldsValue({
      dateTimeZone: nextTimeZone,
      dates: convertDateRangeBetweenTimeZones(dates, selectedTimeZone, nextTimeZone),
    })
  }

  const clearAdvancedField = (field: AdvancedField) => {
    const emptyValues: Record<AdvancedField, unknown> = {
      excludedIsps: undefined,
      restrictedToRegions: undefined,
      weight: undefined,
      defaultSoundFile: undefined,
      abSplit: undefined,
      notificationHotkey: undefined,
    }

    form.setFieldsValue({ [field]: emptyValues[field] })
  }

  const setAdvancedField = (field: AdvancedField, enabled: boolean) => {
    setAdvancedFields(prev => ({ ...prev, [field]: enabled }))
    if (!enabled) {
      clearAdvancedField(field)
    }
  }

  const activeAdvancedFields = Object.entries(advancedFields)
    .filter(([, isActive]) => isActive)
    .map(([field]) => field as AdvancedField)

  const renderCountryTag = ({ closable, value, label, onClose }: {
    closable: boolean;
    value: string;
    label: React.ReactNode;
    onClose: () => void;
  }) => {
    const handleMouseDown = (event: React.MouseEvent<HTMLSpanElement>) => {
      event.preventDefault()
      event.stopPropagation()
    }

    return (
      <Tooltip title={label}>
        <Tag
          onMouseDown={handleMouseDown}
          closable={closable}
          onClose={onClose}
          style={{ marginRight: 3, cursor: "default" }}
        >
          {value}
        </Tag>
      </Tooltip>
    )
  }

  return (
    <Card title="Main info" bordered={false} className={Styles.card}>
      <div className={Styles.primaryGrid}>
        <Form.Item
          label="Name"
          name="name"
          required
          rules={[{
            required: true,
            message: "Event name is required",
          }]}
        >
          <Input placeholder="Enter event name" />
        </Form.Item>

        <Form.Item label="Games" name={["games"]}>
          <Select
            mode="multiple"
            allowClear
            maxTagCount="responsive"
            style={{ width: "100%" }}
            placeholder="Please select"
            options={GAMES_LABELS_LIST.map(({ label, value }) => ({ label, value: value.toString() }))}
            filterOption={(input, option) => (option?.label ?? "").toLowerCase().includes(input.toLowerCase())}
            onChange={handleGameChange}
          />
        </Form.Item>

        <Form.Item
          label="Time-based games"
          name="timeBasedGames"
          tooltip="Games Overwolf sees open and close, with no in-game events. Nothing is hidden: a game that may not deliver is listed with the reason beside it. Numbers are a rough popularity hint, not reach."
          rules={[
            {
              validator: async(_, value: string[] | undefined) => {
                const invalid = invalidTimeBasedIds(value)
                if (invalid.length) throw new Error(invalid.map(describeInvalidId).join("; "))

                const catalog = catalogIdsAmongTimeBased(value)
                if (catalog.length) {
                  throw new Error(
                    `${catalog.join(", ")} has game events — add it under Games instead, or its campaign gets the time-based notification set`,
                  )
                }

                const instanceIds = instanceIdsAmongTimeBased(value)
                if (instanceIds.length) {
                  throw new Error(`${instanceIds.join(", ")} looks like an instance ID — drop the last digit`)
                }
              },
            },
          ]}
        >
          <Select
            mode="multiple"
            allowClear
            style={{ width: "100%" }}
            placeholder="Search by game name or ID"
            options={TIME_BASED_GAME_OPTIONS}
            optionFilterProp="label"
            onChange={handleTimeBasedGameChange}
          />
        </Form.Item>

        {timeBasedGameWarnings.length > 0 && (
          <div className={Styles.timeBasedWarnings} data-testid="time-based-warnings">
            {timeBasedGameWarnings.map(warning => (
              <div key={warning}>⚠ {warning}</div>
            ))}
          </div>
        )}

        <Form.Item
          label="Unlisted game ID"
          tooltip="For a game released since this build. Enter its Overwolf class ID — the launch log line reports it."
          validateStatus={unlistedError ? "error" : undefined}
          help={unlistedError}
        >
          <Space.Compact style={{ width: "100%" }}>
            <Input
              value={unlistedGameId}
              placeholder="e.g. 22584"
              onChange={event => {
                setUnlistedGameId(event.target.value)
                setUnlistedError(undefined)
              }}
              onPressEnter={event => {
                event.preventDefault()
                addUnlistedGameId()
              }}
            />
            <Button onClick={addUnlistedGameId} disabled={!unlistedGameId.trim()}>Add</Button>
          </Space.Compact>
        </Form.Item>

        <Form.Item label="Restricted countries" name="restrictedToCountries">
          <Select
            mode="tags"
            maxTagCount="responsive"
            onChange={handleCountryChange}
            allowClear
            style={{ width: "100%" }}
            placeholder="No restrictions"
            options={COUNTRY_CODES.map(({ code, name }) => ({ label: `${name} (${code})`, value: code }))}
            filterOption={(input, option) => (option?.label ?? "").toLowerCase().includes(input.toLowerCase())}
            tagRender={renderCountryTag}
          />
        </Form.Item>

        <Form.Item
          label="Time zone"
          name="dateTimeZone"
          tooltip="Use local campaign time here. The saved start/end dates are converted to UTC for the backend."
          initialValue={DEFAULT_SPECIAL_EVENT_TIME_ZONE}
        >
          <Select
            showSearch
            options={SPECIAL_EVENT_TIME_ZONE_OPTIONS}
            onChange={handleTimeZoneChange}
            filterOption={(input, option) => (option?.label ?? "").toLowerCase().includes(input.toLowerCase())}
          />
        </Form.Item>

        <Form.Item
          className={Styles.dateRangeItem}
          rules={[{ required: true, message: "Dates are required" }]}
          required
          label={`Start time / End time (${SPECIAL_EVENT_TIME_ZONE_OPTIONS.find(option => option.value === selectedTimeZone)?.label || selectedTimeZone})`}
          name="dates"
          tooltip="Displayed in the selected timezone. Submit converts the selected wall-clock time to UTC."
        >
          <RangePicker
            showTime
            className={Styles.rangePicker}
            format="MMM DD, YYYY HH:mm:ss"
          />
        </Form.Item>
      </div>

      <Collapse ghost className={Styles.advancedCollapse}>
        <Panel
          key="advanced"
          forceRender
          header={(
            <Space size="small" wrap>
              <Text strong>Advanced settings</Text>
              {activeAdvancedFields.length === 0 && <Text className={Styles.mutedText}>Optional fields are hidden</Text>}
              {activeAdvancedFields.map(field => <Tag key={field}>{ADVANCED_FIELD_LABELS[field]}</Tag>)}
            </Space>
          )}
        >
          <div className={Styles.toggleGrid}>
            {(Object.keys(ADVANCED_FIELD_LABELS) as AdvancedField[]).map(field => (
              <label className={Styles.optionalToggle} key={field}>
                <Switch
                  size="small"
                  checked={advancedFields[field]}
                  onChange={checked => setAdvancedField(field, checked)}
                />
                <span>{ADVANCED_FIELD_LABELS[field]}</span>
              </label>
            ))}
          </div>

          <div className={Styles.advancedGrid}>
            {advancedFields.excludedIsps && (
              <Form.Item
                label="Excluded ISPs"
                name="excludedIsps"
                tooltip="Users whose ISP name contains any of these strings (case-insensitive) will not see any notifications from this campaign. E.g. 'google' excludes 'AS15169 Google LLC'."
              >
                <Select
                  mode="tags"
                  allowClear
                  maxTagCount="responsive"
                  style={{ width: "100%" }}
                  placeholder="No ISP exclusions"
                />
              </Form.Item>
            )}

            {advancedFields.restrictedToRegions && (
              <Form.Item
                label="Restricted regions"
                name="restrictedToRegions"
                tooltip="ipdata region codes (e.g. US state codes: MD, VA, CA). Works as an independent filter alongside country restrictions."
              >
                <Select
                  mode="tags"
                  allowClear
                  maxTagCount="responsive"
                  style={{ width: "100%" }}
                  placeholder="No restrictions"
                  options={US_REGION_CODES.map(({ code, name }) => ({ label: `${name} (${code})`, value: code }))}
                  filterOption={(input, option) => (option?.label ?? "").toLowerCase().includes(input.toLowerCase())}
                  onChange={(values: string[]) => {
                    const normalized = Array.from(new Set(values.map(v => v.trim().toUpperCase())))
                    form.setFieldsValue({ restrictedToRegions: normalized })
                  }}
                  tagRender={({ closable, value, label, onClose }) => {
                    const handleMouseDown = (event: React.MouseEvent<HTMLSpanElement>) => {
                      event.preventDefault()
                      event.stopPropagation()
                    }

                    return (
                      <Tooltip title={typeof label === "string" ? label : getRegionNameByCode(String(value))}>
                        <Tag
                          onMouseDown={handleMouseDown}
                          closable={closable}
                          onClose={onClose}
                          style={{ marginRight: 3, cursor: "default" }}
                        >
                          {value}
                        </Tag>
                      </Tooltip>
                    )
                  }}
                />
              </Form.Item>
            )}

            {advancedFields.weight && (
              <Form.Item
                label="Weight"
                name="weight"
                tooltip={[
                  "Priority value used to determine which special event to show.",
                  "If two events have the same weight, they will be shown randomly 50%:50%.",
                  "If one event has a weight of 3, and the other has a weight of 7,",
                  "the event with the weight of 7 will be shown 70% of the time,",
                  "and the event with the weight of 3 will be shown 30% of the time.",
                ].join(" ")}
                rules={[{ type: "number", min: 0, message: "Weight must be a positive number" }]}
              >
                <InputNumber
                  placeholder="Default: 1"
                  style={{ width: "100%" }}
                  min={0}
                />
              </Form.Item>
            )}

            {advancedFields.abSplit && (
              <Form.Item
                label="A/B Split Ratio (0-1)"
                name="abSplit"
                tooltip="Controls user split for A/B testing (0 = nobody hears sounds, 1 = all users hear sounds)"
                rules={[{ type: "number", min: 0, max: 1, message: "Value must be between 0 and 1" }]}
              >
                <InputNumber
                  min={0}
                  max={1}
                  step={0.01}
                  value={abSplit}
                  onChange={value => form.setFieldsValue({ abSplit: value ?? undefined })}
                  placeholder="0 - 1"
                  style={{ width: "100%" }}
                />
              </Form.Item>
            )}

            {advancedFields.defaultSoundFile && (
              <Form.Item
                className={Styles.soundField}
                label="Default Sound File"
                name="defaultSoundFile"
                tooltip="Default sound file to be used when individual moments/banners don't have their own sound specified"
              >
                <div className={Styles.compactSoundUpload}>
                  <UploadSoundComponent
                    soundFile={defaultSoundFile || ""}
                    setSoundFile={soundFile => {
                      form.setFieldsValue({ defaultSoundFile: soundFile })
                    }}
                    placeholder="Upload"
                  />
                </div>
              </Form.Item>
            )}

            {advancedFields.notificationHotkey && (
              <Form.Item
                label="Notification hotkey (event default)"
                name="notificationHotkey"
                tooltip="Default hotkey that opens the active clickable notification's offer in locked-cursor games, e.g. 'Ctrl+Shift+O'. Per-game values override this; leave empty to use the app's built-in default."
                rules={[{
                  validator: async(_, value) => {
                    if (!value || isValidHotkeyCombo(value)) return
                    throw new Error(HOTKEY_VALIDATION_MESSAGE)
                  },
                }]}
              >
                <Input placeholder="Ctrl+Shift+O" allowClear style={{ width: "100%" }} />
              </Form.Item>
            )}
          </div>
        </Panel>
      </Collapse>
    </Card>
  )
}
